// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportBody.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import { observable, computed, action, toJS } from 'mobx';
import ReportPartDetails from './Izenda.ReportPartDetails';
import tracker from 'share/tracking/Izenda.Tracker';
import ReportDesignerConstants, { REPORT_PART_TYPE, OBJECT_STATE } from 'logic/data/Izenda.ReportDesignerConstants';
import constants, { REPORT_PART_MAX_WIDTH, REPORT_PART_MAX_HEIGHT } from '../data/Izenda.ReportBodyConstants';
import ReportPartTypeManager from 'Izenda.ReportPartTypeManager';
import CrossFilteringDetail from './Izenda.CrossFilteringDetail';
import PillDefinition from './Izenda.PillDefinition';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import GridLayout from 'share/utils/Izenda.GridLayoutUtils';
import {
  cloneDeep,
  isEqual,
  isNull,
  invokeMap,
  reject,
  find,
  flow,
  map,
  every,
  isEmpty,
  concat,
  compact,
  filter,
  isNil,
  some,
  getOr,
  head,
  remove,
  flatMap,
  findIndex,
  uniqBy,
  pick,
  forEach,
  invoke,
  get,
  uniqueId,
  assign,
  trim,
  toUpper,
  invokeArgs,
  chain
} from 'lodash/fp';
var GridLayoutUtils = GridLayout(Infinity, REPORT_PART_MAX_WIDTH, REPORT_PART_MAX_HEIGHT);

@tracker
export default class ReportBody {
  crossFilteringDrillFilterInfo = [];
  @observable reportParts = [];
  @observable selectedPart = {};
  @observable configReportPart = null;
  @observable viewMode;
  @observable isFullScreen = false;
  @observable snapToGrid = false;
  @observable crossFilteringDetail = [];

  @computed
  get activeReportParts() {
    return reject('isDelete', this.reportParts);
  }

  @computed
  get supportReportParts() {
    return ReportPartTypeManager.filterSupportedReports(this.activeReportParts);
  }

  @computed
  get hasInvisibleFields() {
    return some('existInvisibleField', this.reportParts);
  }

  @computed
  get hasAnyField() {
    return flow(map('fieldCollection'), every(isEmpty))(this.activeReportParts);
  }

  @computed
  get aggregatedFields() {
    let aggregatedFields = [];

    this.reportParts.forEach(reportPart => {
      aggregatedFields = concat(aggregatedFields, compact(reportPart.aggregatedFieldCollection));
    });

    return aggregatedFields;
  }

  @computed
  get fields() {
    let result = [];

    this.reportParts.forEach(reportPart => {
      result = concat(result, compact(reportPart.fieldCollection));
    });

    return result;
  }
  @computed
  get enableCrossFiltering() {
    var notDefaultParts =
      filter(part => {
        return !isNil(part.type);
      }, this.activeReportParts) || [];

    if (notDefaultParts.length < 1) return false;

    return some(part => {
      const doesSupportDrill = some(
        type => {
          return part.type === type;
        },
        [REPORT_PART_TYPE.Chart, REPORT_PART_TYPE.Gauge, REPORT_PART_TYPE.Map]
      );

      if (!doesSupportDrill) return false;

      return true;
    }, this.activeReportParts);
  }

  @computed
  get enableDeleteAll() {
    return !flow(head, getOr([], 'pillDefinitions'), isEmpty)(this.crossFilteringDetail);
  }
  @computed
  get hadCrossFilteringSetting() {
    return !flow(
      filter(reportPart => {
        return reportPart.reportPartContent.isCrossFiltering;
      }),
      isEmpty
    )(this.activeReportParts);
  }
  @computed
  get crossFilteringReportParts() {
    return flow(
      getOr([], 'activeReportParts'),
      filter(({ headerTitle, reportPartContent }) => !isEmpty(headerTitle) && reportPartContent.isCrossFiltering),
      map(reportPart => reportPart.id)
    )(this);
  }

  constructor(data, viewMode, editMode) {
    this.viewMode = viewMode;
    this.editMode = editMode;
    data && this.populateData(data);
    this.cleanDirty();
  }

  /**
   * Clean filters of cross filtering.
   */
  @action
  cleanDrillInfo() {
    this.crossFilteringDrillFilterInfo = remove(item => {
      const index = flow(
        flatMap(part => {
          return invoke('reportPartContent.getCrossFilteringInfo', part) || getOr([], 'reportPartContent.drilldown.filters', part);
        }),
        findIndex(filter => {
          return filter.key === item.key;
        })
      )(this.activeReportParts);

      return index === -1;
    }, this.crossFilteringDrillFilterInfo);
  }

  /**
   * Update filters of drill data.
   * @param {Object} reportPart
   * @param {Object} drillDownData
   */
  @action
  updateDrillInfo(reportPart: any, drillDownData: any) {
    if (!reportPart.reportPartContent.isCrossFiltering) return drillDownData;

    this.cleanDrillInfo();

    if (isEmpty(drillDownData)) {
      const drillInfo = reportPart.reportPartContent.getDrilldownInfo();
      Array.prototype.push.apply(
        drillInfo.filters,
        ReportPartUtils.transformFilterWithReportPart(reportPart, this.crossFilteringDrillFilterInfo)
      );
      drillInfo.filters = uniqBy(item => JSON.stringify(pick(['key', 'fieldId'], item)), drillInfo.filters);
      return drillInfo;
    }

    const filters = invoke('getCrossFilteringInfo', reportPart.reportPartContent);

    forEach(item => {
      let index = findIndex(filter => {
        return filter.key === item.key;
      }, this.crossFilteringDrillFilterInfo);

      // Add
      if (index === -1) {
        this.crossFilteringDrillFilterInfo.push(item);
      } else {
        this.crossFilteringDrillFilterInfo[index].value = item.value;
      }
    }, filters);
  }

  /**
   * Update cross filtering detail.
   * @param {Object} reportPart
   * @param {Object} drillDownData
   */
  @action
  updateCrossFiltering(reportPart: any, drillDownData: any) {
    const firstCrossFilterDetail = head(this.crossFilteringDetail);
    if (isEmpty(firstCrossFilterDetail) || !reportPart.reportPartContent.isCrossFiltering)
      return this.updateDrillInfo(reportPart, drillDownData);

    const pillDefinitions = get('pillDefinitions', firstCrossFilterDetail);

    const index = findIndex(function (pill) {
      return pill.reportPart.id === reportPart.id;
    }, pillDefinitions);
    const drillInfo = reportPart.reportPartContent.getDrilldownInfo();
    const expandedLevel = getOr(0, 'expandedLevel', drillDownData) || drillInfo.expandedLevel;

    // REMOVE pill
    if (index !== -1 && expandedLevel === 0) {
      pillDefinitions.splice(index, 1);
    }

    // ADD pill
    if (index === -1 && expandedLevel !== 0 && expandedLevel !== -1) {
      pillDefinitions.push(new PillDefinition(reportPart.id, reportPart, getOr('', 'headerTitle', reportPart)));
    }

    // update drill info
    this.updateDrillInfo(reportPart, drillDownData);
  }

  /**
   * Remove a cross filtering item.
   * @param {Object} pill
   */
  @action
  removePill(pill: any) {
    const reportPart = pill.reportPart;
    const firstCrossFilterDetail = head(this.crossFilteringDetail);
    const pillDefinitions = get('pillDefinitions', firstCrossFilterDetail);
    const index = findIndex(pill => {
      return pill.reportPart.id === reportPart.id;
    }, pillDefinitions);

    index !== -1 && pillDefinitions.splice(index, 1);

    reportPart.reportPartContent.resetDrilldownInfo && reportPart.reportPartContent.resetDrilldownInfo();

    this.cleanDrillInfo();
  }

  /**
  * Remove cross filtering items belong to report part.
  * @param {Object} reportPart
  */
  @action
  removePillByReportPart(reportPart: any) {
    const pillDefinitions = flow(
      head,
      get('pillDefinitions')
    )(this.crossFilteringDetail);

    const pillToDelete = filter({ 'id': reportPart.id }, pillDefinitions);

    forEach(pill => {
      const pillId = pill.id;
      const index = findIndex({ 'id': pillId }, pillDefinitions);
      index !== -1 && pillDefinitions.splice(index, 1);
    }, pillToDelete);

    reportPart.reportPartContent.resetDrilldownInfo && reportPart.reportPartContent.resetDrilldownInfo();
    this.cleanDrillInfo();
  }

  /**
   * Remove all cross filtering items
   */
  @action
  removeAllPills() {
    const pillDefinitions = flow(head, get('pillDefinitions'))(this.crossFilteringDetail);

    pillDefinitions && pillDefinitions.clear();
    this.crossFilteringDrillFilterInfo = [];

    forEach(part => {
      part.reportPartContent.resetDrilldownInfo && part.reportPartContent.resetDrilldownInfo();
    }, this.activeReportParts);
  }

  // reset reportbody status include isFullScreen mode and config report part
  @action
  resetReportBodyStatus() {
    this.setConfigReportPart(null);
    this.removeAllPills();
    this.isFullScreen = false;
  }

  // copy config report part status after save if it create new object
  @action
  appliedStatusPreviousConfigReportPart() {
    const previousConfigReportPart = this.configReportPart;

    const previousConfigReportPartName = get('headerTitle', previousConfigReportPart);
    const previousSide = getOr(false, 'isBackSide', previousConfigReportPart);
    const previousConfigField = getOr(null, 'configField', previousConfigReportPart);
    const previousConfigFieldName = getOr(null, 'name', previousConfigField);
    const newConfigReportPart =
      previousConfigReportPart && find({ headerTitle: previousConfigReportPartName }, this.supportReportParts.slice());

    if (!newConfigReportPart) {
      this.resetReportBodyStatus();
      return;
    }

    if (newConfigReportPart !== previousConfigReportPart) {
      this.setConfigReportPart(newConfigReportPart);
      this.changeReportPartSide(newConfigReportPart, previousSide);
    }

    const newConfigField =
      previousConfigField && find({ name: previousConfigFieldName }, newConfigReportPart.fieldCollection.slice());

    newConfigReportPart.setConfigField(newConfigField);
  }

  /**
   * Populate data of list report part
   * @param {Object} data
   * @param {Object} snapToGrid
   * @param {Boolean} keepReportBodyStates
   */
  @action
  populateData(data: any, snapToGrid: any, keepReportBodyStates: boolean, targetReportPart: any = null) {
    snapToGrid = !!snapToGrid;
    const currentReportParts = this.reportParts;
    const isPartialUpdate = targetReportPart && !isEmpty(targetReportPart);

    let currentPart;
    const newReportParts =
      data &&
      !isEmpty(data) &&
      data.map(item => {
        currentPart = find(
          {
            headerTitle: item.title
          },
          currentReportParts
        );
        if(currentPart) {
          if(isPartialUpdate)
            item.id = currentPart.id;
          currentPart.readData(item);
        }
        return currentPart || new ReportPartDetails(item, null, null, this.viewMode, this.editMode);
      });

    if(isPartialUpdate) {
      !newReportParts && this.reportParts.remove(targetReportPart);
    } else {
      this.reportParts.replace(newReportParts || []);
    }

    this.snapToGrid = snapToGrid;

    if (keepReportBodyStates) {
      this.appliedStatusPreviousConfigReportPart();
    } else {
      this.resetReportBodyStatus();
    }

    if (!this.activeReportParts.length) {
      !this.viewMode && this.addReportPart();
    }

    if (isEmpty(this.crossFilteringDetail)) {
      this.crossFilteringDetail.push(new CrossFilteringDetail(null, null, []));
    }
  }

  /**
   * Get dirty status.
   */
  checkDirty() {
    // Check dirty for reportParts.
    const dirtyItems = filter(item => {
      return item.checkDirty && item.checkDirty();
    }, this.reportParts);

    return this.isDirty || (dirtyItems && dirtyItems.length > 0);
  }

  /**
   * Clean dirty status.
   */
  @action
  cleanDirty() {
    // clean dirty for reportParts
    forEach(item => {
      item.cleanDirty && item.cleanDirty();
    }, this.reportParts);

    this.resetDirty();
  }

  /**
   * Add new report part
   * @param {Integer} left
   * @param {Integer} top
   * @param {Integer} width
   * @param {Integer} height
   */
  @action
  addReportPart(left: number, top: number, width: number, height: number) {
    const length = this.reportParts.length;
    const dims = GridLayoutUtils.getDimension(this.activeReportParts, left, top);
    const partX = left || dims.positionX;
    const partY = top || dims.positionY;

    // Ensure report part width/height under its max values
    const useMaxWidth = isNil(width) || width === Infinity || width > constants.REPORT_PART_MAX_WIDTH;
    const useMaxHeight = isNil(height) || height === Infinity || height > constants.REPORT_PART_MAX_HEIGHT;
    const partWidth = useMaxWidth ? constants.REPORT_PART_MAX_WIDTH : width;
    const partHeight = useMaxHeight ? constants.REPORT_PART_MAX_HEIGHT : height;

    if (this.configReportPart && this.configReportPart.isBackSide) {
      // If user is seeing a backside report part, change it back to front-side.
      this.changeReportPartSide(this.configReportPart, false);
    }

    const part = {
      id: uniqueId('reportPart_'),
      positionX: partX,
      positionY: partY,
      width: partWidth,
      height: partHeight,
      isAddNew: true
    };

    const partDetail = new ReportPartDetails(part);

    this.reportParts.splice(length, 0, partDetail);

    // set new added-report part as config report part
    this.setConfigReportPart(partDetail);
    this.markDirty();
  }

  /**
   * Delete a report part
   * @param {Object} reportPart
   */
  @action
  deleteReportPart(reportPart: any) {
    this.isFullScreen = false;

    reportPart.isNew && this.reportParts.remove(reportPart);
    reportPart.delete();

    // reset config report part
    if (this.configReportPart === reportPart) {
      this.setConfigReportPart(null);
    }

    this.removePill({ reportPart: reportPart });

    this.validateCrossFilteringItems();

    this.markDirty();
  }

  /*
   * Validate Cross Filtering Items
   * Disable cross filtering for all report parts when it is not supported.
   */
  validateCrossFilteringItems() {
    if(!this.enableCrossFiltering) {
      forEach(reportPart => {
        reportPart.reportPartContent.isCrossFiltering = false;
      }, this.activeReportParts);
    }
  }

  /**
   * Copy report part
   * @param {Object} reportPart
   */
  @action
  copyReportPart(reportPart: any) {
    const newName = String.generateNewName(reportPart.headerTitle, this.activeReportParts, get('headerTitle'), '()');
    const dims = GridLayoutUtils.getDefaultDimension(this.activeReportParts);
    const partX = dims.positionX;
    const partY = dims.positionY;

    let newReportPart = toJS(reportPart.buildDataForSave());

    newReportPart = assign(newReportPart, {
      id: uniqueId('reportPart_'),
      title: newName,
      isBackSide: false,
      positionX: partX,
      positionY: partY,
      width: newReportPart.width, // constants.REPORT_PART_MAX_WIDTH ,
      height: newReportPart.height, // constants.REPORT_PART_MAX_HEIGHT,
      state: OBJECT_STATE.INSERT
    });

    newReportPart = new ReportPartDetails(newReportPart, true);
    this.reportParts.splice(this.reportParts.length, 0, newReportPart);
    // set new added-report part as config report part
    this.setConfigReportPart(newReportPart);
    this.markDirty();

    return newReportPart;
  }

  /**
   * validate new name unique in report Body
   * @param  {ReportPartDetails} reportPart reportPart need update name
   * @param  {String} name       mew name for report part
   */
  validateNewNameReportPart(reportPart: any, name: string) {
    const commonFormat = flow(trim, toUpper);

    return Promise.resolve(
      !isEmpty(name) &&
        flow(
          reject({
            id: reportPart.id
          }),
          some(flow(get('headerTitle'), commonFormat, isEqual(commonFormat(name))))
        )(this.activeReportParts)
        ? constants.MESSAGES.M02
        : ''
    );
  }

  /**
   * rename report part
   * @param  {ReportPartDetails} reportPart report part need update name
   * @param  {String} name       new report part name
   * @return {Boolean}           update success or not
   */
  renameReportPart(reportPart: any, name: string, defaultName: string) {
    if (isEmpty(name)) {
      this.setDefaultName(reportPart, defaultName);
    } else {
      reportPart.setReportPartName(name);
    }
  }

  /**
   * set config report part
   * @param  {reportPartDetail} reportPart [description]
   */
  @action
  setConfigReportPart(reportPart: any) {
    // Set config mode action for report part form
    if (reportPart && reportPart.reportPartContent) {
      invoke('configModeAction', reportPart.reportPartContent);
    }

    if (reportPart && get('id', this.configReportPart) === get('id', reportPart)) {
      return;
    }

    const isShowBackSide = get('isBackSide', this.configReportPart);

    // switch current repot part to front-side
    if (isShowBackSide) {
      this.changeReportPartSide(this.configReportPart, false);
    }

    if (!reportPart) {
      this.configReportPart = null;

      // config report part = null >> do not apply full screen
      this.resetFullScreenMode();
    } else {
      // current part is in configuration mode (back-side=true)
      // set the current to preview mode, new-config-part to preview mode as well.
      if (isShowBackSide) {
        this.changeReportPartSide(reportPart, false);
      }

      this.configReportPart = reportPart;
    }
  }

  /**
   * Change full screen mode
   * @param  {[type]} reportPart [description]
   * @return {[type]}            [description]
   */
  @action
  changeFullScreenMode(reportPart: any) {
    this.setConfigReportPart(reportPart);
    this.isFullScreen = !this.isFullScreen;
  }

  /**
   * Reset full screen mode
   */
  resetFullScreenMode() {
    this.isFullScreen = false;
  }

  /**
   * Change report part side (from back-side to front-side)
   * @param  {ReportPartDetails}  reportPart
   * @param  {Boolean} isBackSide
   * @return {undefined}
   */
  @action
  changeReportPartSide(reportPart: any, isBackSide: boolean) {
    if (!reportPart) {
      return;
    }
    const newState = isNil(isBackSide) ? !reportPart.isBackSide : isBackSide;

    reportPart.isBackSide = newState;

    invokeArgs('reportPartContent.afterChangeSide', [newState], reportPart);

    // report part in configuration mode (backside=true), set it as config report part
    this.setConfigReportPart(reportPart);
  }

  /**
   * Set view report part
   * @param {ReportPartDetails} reportPart
   * @param {Object} type
   */
  @action
  setViewReportPart(reportPart: any, type: any) {
    // TODO: should manual get lable/icon from registry if not exists.
    reportPart.setReportPartType(type.value);
    reportPart.isBackSide = true;
    this.setConfigReportPart(reportPart);
    this.setDefaultName(reportPart, type.label);
  }

  /**
   * set default report part name
   * @param {ReportPartDetails} reportPart
   * @param {String} defaultName
   */
  setDefaultName(reportPart: any, defaultName: string) {
    const newName = String.generateNewName(
      defaultName,
      reject(
        {
          id: reportPart.id
        },
        this.activeReportParts
      ),
      get('headerTitle')
    );

    reportPart.setReportPartName(newName);
  }


  /**
   * function for returning the details of each row i.e. it is expanded or not.
   * @param {*} obj current report records with their  drill down
   */
  buildCollapsableItems(obj) {
    let collapsableObj = {
      value: '',
      key: '',
      childs: [],
      isExpand: false,
    }
    if (obj && obj.childs) {
      if (obj.childs.length === 0) {
        collapsableObj.value = obj.value;
        collapsableObj.key = obj.key;
        collapsableObj.isExpand = obj.isExpand;
        collapsableObj.columnName = obj.uniqueFieldNameAlias;
        return collapsableObj;
      }
      collapsableObj.value = obj.value;
      collapsableObj.key = obj.key;
      collapsableObj.isExpand = obj.isExpand;
      collapsableObj.columnName = obj.uniqueFieldNameAlias;
      let childs = [];
      // let childs = map((data) => getDesireObj(data.childs),obj.childs)
      for (let count = 0; count < obj.childs.length; count++) {
        childs.push(this.buildCollapsableItems(obj.childs[count]));
      }
      collapsableObj.childs = childs;
      return collapsableObj;
    }
    return {};
  }

  /**
   * function for making separator info.
   * @param {*} separatorData
   */
  getSeparatorInfo(separatorData) {
    if (separatorData) {
      return {
        isExpand: separatorData.isExpand,
        separatorInfo: separatorData.keys.map(data => ({
          key: data.columnName,
          value: data.value,
          level: data.level,
          columnName: getOr('', 'element.field.fieldName', data),
          fieldAlias: getOr('', 'element.field.fieldNameAlias', data),
        }))
      }
    }
    return {
      isExpand: false,
      separatorInfo: []
    };
  }

  /**
   * function for returning the details of each row i.e. it is expanded or not.
   * @param {*} currentRecords current report records with their  drill down and separator info.
   */
  buildReportView(currentRecords, isMaintainState = false) {
    if(isMaintainState) {
      return currentRecords.map(obj => {
        const isLogicalSeparator = getOr(false, 'separatorInfo.isLogicalSeparator', obj);
        let collapsableObj = {
          separatorList: this.getSeparatorInfo(obj.separatorInfo),
          rowCollapsableList: isLogicalSeparator
            ? this.buildCollapsableItems({
              value: '',
              key: 'All',
              childs: obj.records,
              isExpand : true
            })
            : this.buildCollapsableItems(obj.records)
        }
        return collapsableObj;
      })
    }
    return [];
  }

  /**
   * Build data for report saving
   */
  buildDataForSaving(isMaintainState = false) {
    const data = flow(
      filter(item => !isNil(item.type)),
      map(item => {
        return (item.buildDataForSave && item.buildDataForSave()) || item;
      }),
    )(this.reportParts);
    data.map(item => {
      item.reportPartContent && item.reportPartContent.rows &&
      (item.reportPartContent.rows['collapsableList'] = this.buildReportView(item.reportInfo, isMaintainState));
    });
    map(item => {
      delete item.reportInfo
      return item;
    }, data)

    return {
      reportPart: data,
      snapToGrid: !!this.snapToGrid
    };
  }

  /**
   * Remove unuse dynamic threshold
   */
  @action
  removeUnuseDynamicThresholds() {
    flow(
      filter({ type: REPORT_PART_TYPE.Chart }),
      forEach(
        item =>
          item.reportPartContent.removeUnuseDynamicThresholds && item.reportPartContent.removeUnuseDynamicThresholds())
    )(this.reportParts);
  }

  /**
   * Mark all field visible
   */
  @action
  markVisibleAllField() {
    this.reportParts.forEach(function (reportPart) {
      reportPart.setAllFieldVisible();
    });
    this.markDirty();

    return Promise.resolve();
  }

  /**
   * Mark all field visible
   * @param {String} id
   * @param {String} name
   * @param {Object} updaptedCalField
   */
  @action
  updateElementInfo(id: string, name: string, updaptedCalField: any) {
    let changed = false;
    const originalField = find(fieldDetail => {
      return fieldDetail.field.fieldId === id;
    }, this.fields);

    if (isNil(originalField)) return;

    // update report parts
    this.reportParts.forEach(function (reportPart) {
      const currentChanged = reportPart.updateField(
        id,
        cloneDeep(originalField.field.fieldName),
        name,
        updaptedCalField
      );

      changed = changed || currentChanged;
    });
    changed && this.markDirty();
    return changed;
  }

  /**
   * Check report body has use calculated field
   * @param {String} id
   * @param {String} name
   */
  hasCalculatedField(id: string, name: string) {
    return some(reportPart => {
      return reportPart.hasCalculatedField(id, name);
    }, this.reportParts);
  }

  /**
   * Clean all cross filtering
   */
  @action
  cleanCrossFiltering() {
    invokeMap('setCrossFiltering', this.reportParts, false);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportBody.js